import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import PublicBlogHeader from "../components/PublicBlogHeader";
import { getBlogPostBySlug } from "../services/blogService";
import "./PublicBlog.css";

function formatDate(value) {
  if (!value) return "";
  const date = value.toDate ? value.toDate() : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("en", {
    year: "numeric",
    month: "long",
    day: "numeric",
  }).format(date);
}

function ArticleContent({ content }) {
  return (
    <div className="public-blog-article-content">
      {content.split(/\n{2,}/).map((block, index) => {
        const value = block.trim();
        if (!value) return null;
        if (value.startsWith("### ")) {
          return <h3 key={index}>{value.replace(/^###\s+/, "")}</h3>;
        }
        if (value.startsWith("## ")) {
          return <h2 key={index}>{value.replace(/^##\s+/, "")}</h2>;
        }
        return <p key={index}>{value}</p>;
      })}
    </div>
  );
}

export default function PublicBlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const loadPost = async () => {
      try {
        setLoading(true);
        setError("");
        const data = await getBlogPostBySlug(slug);
        setPost(data);
        document.title = data
          ? `${data.seoTitle || data.title} | Darssy`
          : "Article not found | Darssy";
      } catch (err) {
        console.error("Failed to load published blog post:", err);
        setError("Could not load this article. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    loadPost();
  }, [slug]);

  return (
    <div className="public-blog-page">
      <PublicBlogHeader />
      <main>
        {loading ? (
          <div className="public-blog-state">Loading article...</div>
        ) : error ? (
          <div className="public-blog-state public-blog-error">{error}</div>
        ) : !post ? (
          <div className="public-blog-state">
            <div>
              <h1>Article not found</h1>
              <p>This article is unavailable or has not been published.</p>
              <Link to="/blogs" className="public-blog-button public-blog-button-primary">
                Back to blog
              </Link>
            </div>
          </div>
        ) : (
          <article>
            <header className="public-blog-article-header">
              <div className="public-blog-container">
                <div className="public-blog-kicker">{post.category}</div>
                <h1>{post.title}</h1>
                <div className="public-blog-article-meta">
                  {post.author || "Darssy Team"}
                  {post.publishedAt ? ` · ${formatDate(post.publishedAt)}` : ""}
                </div>
              </div>
              {post.coverImageUrl && (
                <img
                  className="public-blog-article-cover"
                  src={post.coverImageUrl}
                  alt={post.title}
                />
              )}
            </header>

            <div className="public-blog-article-layout">
              <ArticleContent content={post.content || ""} />
              <section className="public-blog-cta">
                <h2>Build your teaching system with Darssy</h2>
                <p>
                  Manage students, courses, communication, and payments from one professional
                  platform.
                </p>
                <div className="public-blog-cta-actions">
                  <Link to="/" className="public-blog-button public-blog-button-primary">
                    Explore Darssy
                  </Link>
                  <Link to="/teacher/login" className="public-blog-button">
                    Teacher login
                  </Link>
                </div>
              </section>
            </div>
          </article>
        )}
      </main>
    </div>
  );
}
